import { Autocomplete, TextField, CircularProgress, Typography, Box } from '@mui/material';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { css } from '@emotion/react';
import { useAccountSwitcher, type TenantWithSubdomain } from '../../services/hooks/useAccountSwitcher';

interface AccountSwitcherProps {
  onCloseMenu: () => void;
}

const containerStyle = css`
  padding: 8px 16px;
  min-width: 250px;
`;

const loadingContainerStyle = css`
  padding: 8px 16px;
  min-width: 250px;
  display: flex;
  justify-content: center;
`;

const errorTextStyle = css`
  padding: 8px 0;
  color: #d32f2f;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export function AccountSwitcher({ onCloseMenu }: AccountSwitcherProps) {
  const {
    tenants,
    currentTenant,
    isLoading,
    isSwitching,
    error,
    hasMultipleTenants,
    isInitialTenantLoad,
    handleSwitchTenant,
  } = useAccountSwitcher();

  // While tenants fetch, the filtered count is unknown — show a plain spinner
  // rather than the full switcher, which internal-tenant users may never get
  if (isInitialTenantLoad && !hasMultipleTenants) {
    return (
      <Box css={loadingContainerStyle}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Keep rendering on error so the failure is visible even when the
  // switchable-tenant list is empty (e.g. the tenant fetch failed)
  if (!hasMultipleTenants && !error) {
    return null;
  }

  const handleChange = (_event: unknown, value: TenantWithSubdomain | null) => {
    if (value && value.tenantId !== currentTenant?.tenantId) {
      handleSwitchTenant(value.tenantId);
      onCloseMenu();
    }
  };

  // The current account may not be switchable (e.g. an internal tenant filtered
  // out of the options), so resolve the Autocomplete value from the options list
  const currentTenantOption = tenants.find((tenant) => tenant.tenantId === currentTenant?.tenantId) ?? null;

  return (
    <Box css={containerStyle}>
      {error && (
        <Box css={errorTextStyle}>
          <ErrorOutlineIcon fontSize='small' />
          <Typography variant='body2'>{error}</Typography>
        </Box>
      )}

      <Autocomplete<TenantWithSubdomain, false, false, false>
        value={currentTenantOption}
        onChange={handleChange}
        options={tenants}
        getOptionLabel={(option) => option.name}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.tenantId}>
              <Box>
                <Typography variant='body2'>{option.name}</Typography>
                <Typography variant='caption' color='textSecondary'>
                  {`${option.subdomain}.foresightdata.com`}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Switch Account'
            size='small'
            InputProps={{
              ...params.InputProps,
              startAdornment: <SwitchAccountIcon sx={{ mr: 1, color: 'action.active' }} />,
              endAdornment: (
                <>
                  {isLoading || isSwitching ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        disabled={isLoading || isSwitching}
        isOptionEqualToValue={(option, value) => option.tenantId === value?.tenantId}
        ListboxProps={{
          style: { maxHeight: '250px' },
        }}
      />
    </Box>
  );
}
